'use client';

import { analyticsEvents } from '@/components/analytics/GoogleAnalytics';
import { ArrowRight } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useRef } from 'react';
import { StoneInvestmentCurrencySwitcher } from './CurrencySwitcher';
import { StoneInvestmentLocaleSwitcher } from './LocaleSwitcher';

export interface MenuOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  locale?: 'en' | 'es' | 'fr';
  messages?: Record<string, unknown> | undefined;
}

/**
 * Stone Investment Menu Overlay
 * 
 * Concept One Design (Exact Match):
 * - Full-screen overlay with slide-down animation
 * - Left panel with decorative image and Givenchy quote
 * - Right panel with numbered navigation (01-07)
 * - Design Lab entry with Beaker icon
 * - Bottom: Contact info + Client Login
 */
export function StoneInvestmentMenuOverlay({ 
  isOpen, 
  onClose, 
  locale = 'en',
  messages
}: MenuOverlayProps) {
  const stoneMessages = (messages?.stone || {}) as Record<string, unknown>;
  const menuMessages = (stoneMessages.menu || {}) as Record<string, string>;
  const decorativeMessages = (stoneMessages.decorative || {}) as Record<string, string>;
  const accessMessages = (stoneMessages.accessibility || {}) as Record<string, string>;
  
  const t = (key: string, fallback: string): string => {
    return menuMessages[key] || fallback;
  };

  // Auto-close the overlay whenever the route changes. Without this, a fast
  // tap on a menu item on mobile could trigger navigation while the onClick
  // close handler was still pending, leaving the overlay open over the new
  // page. Watching the pathname guarantees the overlay always dismisses
  // after navigation completes.
  const pathname = usePathname();
  const lastPathRef = useRef<string | null>(pathname);
  useEffect(() => {
    if (lastPathRef.current !== pathname) {
      lastPathRef.current = pathname;
      if (isOpen) onClose();
    }
  }, [pathname, isOpen, onClose]);

  // Contact elevated after Collection / Firm for quicker enquire path
  const menuItems = [
    { label: t('theCollection', 'The Collection'), sub: t('theCollectionSub', 'Curated Properties'), href: `/${locale}/properties` },
    { label: t('theFirm', 'The Firm'), sub: t('theFirmSub', 'Vision & Heritage'), href: `/${locale}/about` },
    { label: t('contact', 'Contact'), sub: t('contactSub', 'Get in Touch'), href: `/${locale}/contact` },
    { label: t('mauritius', 'Mauritius'), sub: t('mauritiusSub', 'Indian Ocean'), href: `/${locale}/properties?region=mauritius` },
    { label: t('provence', 'Provence'), sub: t('provenceSub', 'South of France'), href: `/${locale}/properties?region=provence` },
    { label: t('valuations', 'Valuations'), sub: t('valuationsSub', 'Precision & Value'), href: `/${locale}/valuations` },
    { label: t('journal', 'Journal'), sub: t('journalSub', 'Lifestyle & News'), href: `/${locale}/news` },
  ];
  const enquireHref = `/${locale}/contact`;
  const enquireLabel = ((stoneMessages.navigation || {}) as Record<string, string>).enquire || 'Enquire';

  // A coarse pointer (finger) gets the simpler, more reliable mobile layout:
  // - No staggered transition delays (every item is immediately tappable)
  // - No transform-on-hover (the label can't slide away from the finger)
  // - Full-width tap targets with generous vertical padding
  // - Top padding clears the navigation bar with margin to spare
  //
  // The overlay sits at z-40 so the parent <nav> at z-50 keeps the toggle
  // (Menu / X) accessible. The nav's logo + search controls are explicitly
  // pointer-events-none while the menu is open so they cannot intercept a
  // tap meant for the first menu item.
  return (
    <div
      id="stone-mobile-menu"
      className={`fixed inset-0 z-40 bg-stone-900 text-stone-50 transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${
        isOpen ? 'translate-y-0' : '-translate-y-full pointer-events-none'
      }`}
      aria-hidden={!isOpen}
      inert={!isOpen ? true : undefined}
      role="dialog"
      aria-modal="true"
      aria-label={accessMessages.mainNavigation || 'Main navigation'}
    >
      <div className="h-full w-full flex flex-col md:flex-row">
        {/* Left: Decorative / Image Panel (desktop only) */}
        <div className="hidden md:block w-1/3 h-full bg-stone-800 relative overflow-hidden">
          <Image 
            src="https://res.cloudinary.com/elementary-media/image/upload/v1772727446/Generated_Image_March_05_2026_-_8_16PM_jwhwts.png" 
            className={`absolute inset-0 w-full h-full object-cover transition-all duration-[2000ms] ${
              isOpen ? 'scale-100 opacity-60' : 'scale-110 opacity-0'
            }`}
            alt={accessMessages.menuImageAlt || 'Menu Aesthetic'}
            fill
            sizes="33vw"
            priority={false}
          />
          <div className="absolute bottom-12 left-12 right-12">
            <p className="font-serif text-3xl italic text-stone-50">
              {decorativeMessages.luxuryQuote || '"Luxury is in each detail."'}
            </p>
            <p className="mt-4 text-sm tracking-widest uppercase text-stone-300 font-sans">
              {decorativeMessages.quoteAuthor || 'Hubert de Givenchy'}
            </p>
          </div>
        </div>

        {/* Right: Navigation Links
            pt-32 on mobile gives 128px of clearance — comfortably more than
            the ~116px navigation bar (py-8 + 52px logo) so the first menu
            item is always fully visible below it, with margin to spare for
            iOS safe-area-inset (handled via env() at the body level).
            The panel scrolls independently (overscroll-contain) so menu
            scrolling never leaks to the page beneath. */}
        <nav
          className="w-full md:w-2/3 h-full flex flex-col md:justify-center px-5 sm:px-8 md:px-24 pt-32 sm:pt-36 md:pt-28 pb-8 md:pb-8 overflow-y-auto overscroll-contain"
          aria-label={accessMessages.mainNavigation || 'Main navigation'}
        >
          <ul className="flex flex-col">
            {menuItems.map((item, idx) => (
              <li key={item.href} className="border-b border-stone-800/60 md:border-0 last:border-b-0">
                <Link
                  href={item.href}
                  onClick={onClose}
                  prefetch
                  className="group block py-4 md:py-3 -mx-2 px-2 rounded-sm touch-manipulation focus-visible:outline-2 focus-visible:outline-stone-50 active:bg-stone-800/40 transition-colors"
                >
                  <div
                    className={`flex items-baseline gap-4 sm:gap-6 md:gap-6 transition-opacity duration-500 ${
                      isOpen ? 'opacity-100' : 'opacity-0'
                    }`}
                    // Stagger only on md+ where the transform-y animation
                    // is visible. On mobile every item is immediately
                    // tappable as soon as the overlay slides in.
                    style={{ transitionDelay: isOpen ? `${idx * 60}ms` : '0ms' }}
                  >
                    <span className="text-xs sm:text-sm text-stone-400 font-sans uppercase tracking-widest w-10 sm:w-14 md:w-24 shrink-0 pt-1 md:pt-0">
                      0{idx + 1}
                    </span>
                    <span className="font-serif text-3xl sm:text-4xl md:text-6xl leading-tight md:transition-transform md:duration-500 md:group-hover:translate-x-4">
                      {item.label}
                    </span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
          
          {/* Bottom Section - Enquire weight + Contact Info & Private Access */}
          <div 
            className={`mt-10 sm:mt-16 md:mt-24 pt-6 sm:pt-8 border-t border-stone-800 flex flex-col gap-6 sm:gap-8 transition-opacity duration-1000 ${
              isOpen ? 'opacity-100' : 'opacity-0'
            }`}
            style={{ transitionDelay: '500ms' }}
          >
            <Link
              href={enquireHref}
              onClick={() => {
                analyticsEvents.custom('cta_click', {
                  cta_text: enquireLabel,
                  cta_location: 'nav',
                  cta_destination: enquireHref,
                  cta_variant: 'menu_enquire',
                });
                onClose();
              }}
              prefetch
              className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-white text-stone-900 text-xs uppercase tracking-[0.2em] font-sans hover:bg-stone-200 transition-colors duration-500 w-full md:w-auto md:self-start"
            >
              {enquireLabel}
              <ArrowRight size={16} aria-hidden="true" />
            </Link>

            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 sm:gap-8">
              <div className="text-stone-300 text-xs sm:text-sm font-sans space-y-1">
                <p>{t('locations', 'Provence • Mauritius')}</p>
                <p>
                  <a href="tel:+33488043807" className="hover:text-stone-100 transition-colors">
                    +33 4 88 04 38 07
                  </a>
                </p>
                <div className="flex items-center gap-6 pt-4 text-stone-300">
                  <StoneInvestmentCurrencySwitcher />
                  <StoneInvestmentLocaleSwitcher locale={locale} variant="minimal" />
                </div>
              </div>
              
              <Link 
                href={`/${locale}/login`}
                onClick={onClose}
                prefetch
                className="group relative overflow-hidden touch-manipulation focus-visible:outline-2 focus-visible:outline-stone-50"
              >
                <div className="relative px-5 py-3 sm:px-8 sm:py-4 border border-stone-400 hover:border-stone-50 transition-all duration-500">
                  <div className="absolute inset-0 bg-stone-50 transform -translate-x-full group-hover:translate-x-0 transition-transform duration-500 ease-out" />
                  <div className="relative flex items-center gap-3 sm:gap-4">
                    <span className="font-sans text-[10px] sm:text-xs uppercase tracking-[0.2em] sm:tracking-[0.25em] text-stone-50 group-hover:text-stone-900 transition-colors duration-500">
                      {t('privateCollection', 'Private Access')}
                    </span>
                    <ArrowRight 
                      size={16} 
                      className="text-stone-400 group-hover:text-stone-900 group-hover:translate-x-1 transition-all duration-500" 
                      aria-hidden="true" 
                    />
                  </div>
                </div>
              </Link>
            </div>
          </div>
        </nav>
      </div>
    </div>
  );
}

export default StoneInvestmentMenuOverlay;
